import { cn } from "@/lib/utils";
import type { TypologyResult } from "@/lib/api";
import {
  UsersIcon,
  BriefcaseIcon,
  ZapIcon,
  WalletIcon,
  CarIcon,
  SparklesIcon,
  LeafIcon,
} from "lucide-react";
import type { FC } from "react";

const archetypeConfig: Record<
  string,
  { icon: FC<{ className?: string }>; accent: string }
> = {
  "family-EV-curious-midrange": {
    icon: LeafIcon,
    accent: "from-emerald-500/10 to-teal-500/10 border-emerald-300",
  },
  "family-practical-conservative": {
    icon: UsersIcon,
    accent: "from-blue-500/10 to-sky-500/10 border-blue-300",
  },
  "young-professional-city-commuter": {
    icon: CarIcon,
    accent: "from-violet-500/10 to-purple-500/10 border-violet-300",
  },
  "fleet-manager-deductibility-focused": {
    icon: BriefcaseIcon,
    accent: "from-amber-500/10 to-orange-500/10 border-amber-300",
  },
  "price-sensitive-first-time-buyer": {
    icon: WalletIcon,
    accent: "from-rose-500/10 to-pink-500/10 border-rose-300",
  },
  "enthusiast-performance-focused": {
    icon: ZapIcon,
    accent: "from-red-500/10 to-orange-500/10 border-red-300",
  },
};

const defaultConfig = {
  icon: SparklesIcon,
  accent: "from-neutral-500/10 to-slate-500/10 border-neutral-300",
};

export function TypologyCard({ data }: { data: TypologyResult }) {
  const config = archetypeConfig[data.typology] ?? defaultConfig;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "my-3 rounded-xl border bg-gradient-to-br p-4 shadow-sm",
        config.accent,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm">
          <Icon className="size-5 text-neutral-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 text-sm leading-tight">
            {data.typologyLabel}
          </p>
          <p className="mt-1 text-neutral-600 text-xs leading-relaxed">
            {data.description}
          </p>
        </div>
      </div>

      {data.recommendations.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.recommendations.map((rec) => (
            <span
              key={rec}
              className="inline-flex items-center rounded-full bg-white/70 px-2.5 py-0.5 text-neutral-700 text-[11px] font-medium shadow-sm"
            >
              {rec}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
